import React from "react";
import { Redirect } from "react-router";
import { Query } from "react-apollo";
import * as R from "ramda";
import { Alert, Spin, notification } from "antd";
import { css } from "glamor";
import { TunnelPlaceholder } from "react-tunnels";
import { BASE_URL, DEBUG } from "../../config";
import authFetch from "../../api/authFetch";
import { Comments } from "../../component/Comments";
import WithAuth from "../../container/WithAuth";
import Country from "../../component/Country";
import TaskHeader from "./TaskHeader";
import { TaskComponents } from "./TaskComponents";
import { GET_TASK_DATA } from "./queries";

const styles = {
    taskWrapper: css({
        margin: "2rem 0 1rem",
    }),
    entityName: css({
        fontWeight: "bolder",
    }),
    alert: css({
        marginBottom: "1rem",
    }),
    preventTaskWrapper: css({
        opacity: 0.5,
        pointerEvents: "none",
        userSelect: "none",
    }),
    entityDetailsContainer: css({
        display: "flex",
        padding: "24px 0",
        margin: "24px 0",
        borderTop: "1px solid #e8e8e8",
        borderBottom: "1px solid #e8e8e8",
    }),
    entityNameContainer: css({
        width: 335,
    }),
};

class HumanTask extends React.Component {
    state = {
        isCompleting: false,
    };

    handleAssign = e => {
        e.preventDefault();

        const taskId = this.props.match.params.id;

        this.props.history.push(`/tasks/${taskId}/claim/`);
    };

    handleUnassign = async e => {
        e.preventDefault();

        const taskId = this.props.match.params.id;

        const req = new Request(`${BASE_URL}/api/tasks/${taskId}/unassign/`, {
            method: "POST",
            body: {},
        });

        try {
            await authFetch(req);
        } catch (err) {
            notification.error({
                message: "Failed to unassign",
                description:
                    "Sorry, we failed to unassign this task. Please try again later.",
            });

            throw err;
        }
        this.props.history.push("/tasks/");
    };

    handleComplete = fallback_entity_id => async output => {
        this.setState({ isCompleting: true });

        const taskId = this.props.match.params.id;

        const req = new Request(`${BASE_URL}/api/tasks/${taskId}/output/`, {
            method: "POST",
            body: JSON.stringify(output),
        });

        try {
            const response = await authFetch(req);
            const { next_task_id, entity_id } = await response.json();
            if (next_task_id) {
                this.props.history.push(`/tasks/${next_task_id}/claim/`);
            } else if (entity_id) {
                this.props.history.push(`/entities/${entity_id}`);
            } else {
                this.props.history.push("/tasks/");
            }
            return;
        } catch (err) {
            if (err.response.status == 409) {
                const { next_task_id } = await err.response.json();
                notification.warning({
                    message: "Task Already Completed",
                    duration: 0,
                    description: (
                        <p>
                            This task has already been completed elsewhere. This
                            may be a result of a connection error during a
                            previous action. Try{" "}
                            <a href={`/beta/tasks/${next_task_id}`}>
                                continuing to the next available task
                            </a>
                            , or{" "}
                            <a
                                href={`/beta/entities/${fallback_entity_id}/research-items/`}
                            >
                                return to the research items list
                            </a>
                            .
                        </p>
                    ),
                });
            } else {
                notification.error({
                    message: "Failed to complete task",
                    description:
                        "Sorry, an error occurred while trying to complete the task. We have been notified about the problem, but this may be caused by intermittent network issues. Please try again later.",
                });

                throw err;
            }
        } finally {
            this.setState({ isCompleting: false });
        }
    };

    renderEntityDetails = entity => {
        if (!entity) {
            return null;
        }

        return (
            <React.Fragment>
                <div className={styles.entityDetailsContainer}>
                    <div className={styles.entityNameContainer}>
                        <div {...styles.entityName}>{entity.name}</div>

                        <div>
                            {entity.entityType === "ORGANISATION"
                                ? "Country of organisation: "
                                : "Country of address: "}
                            <Country.Name>
                                {entity.primaryCountryCode}
                            </Country.Name>
                        </div>
                    </div>
                    <TunnelPlaceholder id="humantask-entity-details" />
                </div>
            </React.Fragment>
        );
    };

    getCommentTags = data => {
        return data.node.commentTags ? JSON.parse(data.node.commentTags) : {};
    };

    render() {
        const taskId = this.props.match.params.id;
        const id = btoa("Task:" + taskId);

        const isDebugging =
            DEBUG || (this.props.location.search || "").includes("debug");

        return (
            <Query
                query={GET_TASK_DATA}
                fetchPolicy="network-only"
                variables={{ id }}
            >
                {({ data, loading }) => {
                    if (loading) {
                        return <Spin tip="Loading task..." />;
                    }

                    if (!data.node) {
                        return (
                            <Alert
                                message="Task does not exist."
                                type="error"
                                showIcon
                            />
                        );
                    }

                    if (!data.node.componentName) {
                        return <Redirect to="/tasks/" />;
                    }

                    const taskInput = data.node.inputs;
                    const entity = data.node.entity;
                    const status = R.path(["node", "status"], data);
                    const assignee = R.path(["node", "owner", "id"], data);

                    const taskProperties = R.pipe(
                        R.path(["node", "properties", "edges"]),
                        R.indexBy(R.path(["node", "name"]))
                    )(data);
                    const entityRequestUlid = R.path(
                        ["entity_request_ulid", "node", "value"],
                        taskProperties
                    );

                    const isSubProcess =
                        data.node.process.uid !== data.node.rootProcess.uid;

                    const isCompleted = status === "DONE";
                    const isAssigned = status === "ASSIGNED" || assignee;
                    const isAssignee =
                        parseInt(assignee) ===
                        parseInt(this.props.auth.user_id);

                    const canModify =
                        !this.state.isCompleting && !isCompleted && isAssignee;
                    const canClaimTask = !isCompleted && !isAssigned;
                    const canUnassignTask = !isCompleted && isAssignee;

                    const HumanTaskComponent =
                        TaskComponents[data.node.componentName];

                    const { source_context } = taskInput;

                    const sourceInformation =
                        source_context &&
                        `${source_context.category.name} - ${source_context.source.name}`;

                    const inputEntityUlid = R.path(
                        ["_properties", "entity_ulid"],
                        data.node.inputs
                    );

                    return (
                        <React.Fragment>
                            <TaskHeader
                                sourceInformation={sourceInformation}
                                title={data.node.title}
                                userInstructions={data.node.userInstructions}
                                canClaimTask={canClaimTask}
                                canUnassignTask={canUnassignTask}
                                onAssign={this.handleAssign}
                                onUnassign={this.handleUnassign}
                                entityRequestUlid={entityRequestUlid}
                            />

                            {isDebugging && (
                                <Alert
                                    message="Debug (only shown in development)"
                                    description={
                                        <ul>
                                            <li>
                                                This task is part of:{" "}
                                                {data.node.process.flowClass}
                                            </li>
                                            {isSubProcess && (
                                                <li>
                                                    It is a subprocess in:{" "}
                                                    {
                                                        data.node.rootProcess
                                                            .flowClass
                                                    }
                                                </li>
                                            )}
                                        </ul>
                                    }
                                    type="info"
                                    showIcon
                                    closable
                                />
                            )}

                            {this.renderEntityDetails(entity)}

                            {!isAssigned && (
                                <Alert
                                    {...styles.alert}
                                    message="This task cannot be modified as it is currently unassigned."
                                    type="warning"
                                    showIcon
                                />
                            )}

                            {isAssigned && !isAssignee && (
                                <Alert
                                    {...styles.alert}
                                    message="This task cannot be modified as it is currently assigned to another user."
                                    type="warning"
                                    showIcon
                                />
                            )}

                            {isCompleted && (
                                <Alert
                                    {...styles.alert}
                                    message="This task cannot be modified as it has already been completed."
                                    type="warning"
                                    showIcon
                                />
                            )}

                            <Spin
                                spinning={this.state.isCompleting}
                                tip="Completing task..."
                            >
                                <div
                                    {...styles.taskWrapper}
                                    className={
                                        !canModify
                                            ? `${styles.preventTaskWrapper}`
                                            : ""
                                    }
                                >
                                    <React.Suspense
                                        fallback={<p>Loading...</p>}
                                    >
                                        <HumanTaskComponent
                                            processId={
                                                data.node.rootProcess.uid
                                            }
                                            taskId={taskId}
                                            taskInput={taskInput}
                                            entity={entity}
                                            refreshToken={this.props.auth.raw}
                                            username={this.props.auth.username}
                                            userId={this.props.auth.user_id}
                                            displayName={
                                                this.props.auth.displayName
                                            }
                                            onComplete={this.handleComplete(
                                                inputEntityUlid
                                            )}
                                        />
                                    </React.Suspense>
                                </div>
                            </Spin>

                            <div {...styles.taskWrapper}>
                                <h3>Comments</h3>
                                <Comments
                                    key={data.node.rootProcess.uid}
                                    roomID={data.node.rootProcess.uid}
                                    tags={this.getCommentTags(data)}
                                />
                            </div>
                        </React.Fragment>
                    );
                }}
            </Query>
        );
    }
}

export default WithAuth(HumanTask);
